import { css, cx } from '@linaria/core'
import type { ComponentProps, FC, ReactNode } from 'react'
import { HoverTooltip } from '#components/HoverTooltip'
import { fragments, style } from '#styles'
import { borderRadius } from '#styles/borderRadius'
import { borderWidth } from '#styles/borderWidth'
import { motion } from '#styles/motion'
import { spacing } from '#styles/spacing'

const ButtonClass = css`
  appearance: none;
  cursor: pointer;
  border: none;
  white-space: nowrap;
  transition: all ${motion.fast} ease;
  border-radius: ${borderRadius.sharp};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sp4};

  > span {
    ${fragments.textStyle.body.m.condensedBold}
  }

  /* todo: check if there use cases for XS, S, XL and XXL. If yes add to textstyles and replace font size with text style */

  &:not(.custom) {
    /* size l as default */
    ${fragments.textStyle.body.m.condensedBold}
    padding: 10px 14px;
    
    &.size-xs {
      padding: ${spacing.sp4} 5px;
      ${fragments.textStyle.body.xs.condensedBold};
    } 
  
    &.size-s {
      padding: 7px ${spacing.sp8};;
      ${fragments.textStyle.body.s.condensedBold};
    }
  
    &.size-m {
      padding: 10px 14px;
      ${fragments.textStyle.body.s.condensedBold}
    }
  
    &.size-l {
      padding: ${spacing.sp12} ${spacing.sp16};
      ${fragments.textStyle.body.m.condensedBold}
    }
  }

  &:disabled {
    cursor: default;
    color: ${style.theme.textDisabled};
    background: ${style.theme.backgroundSurfacePrimary};
    transition: background-color ${motion.fast} ease;
  }
  
  @layer components {
    &.custom {
      background: transparent;
    }
  }

  &.accent,
  &.primary {
    &:disabled {
      cursor: default;
      border: none;
      color: ${style.theme.textDisabled};
      background: ${style.theme.backgroundSurfaceDisabled};
      transition: background-color ${motion.fast} ease;

      &:hover {
        background: ${style.theme.backgroundSurfaceDisabled};
      }
    }
  }

  &.accent {
    border: none;
    color: ${style.theme.textWhite};
    background: ${style.theme.backgroundSurfaceAccentDefault};

    &:hover {
      background: ${style.theme.backgroundSurfaceAccentStrong};
      transition: background-color ${motion.fast} ease;
    }
  }

  &.primary {
    border: none;
    color: ${style.theme.textWhite};
    background: ${style.theme.backgroundSurfaceStandardDefault};

    &:hover {
      background: ${style.theme.backgroundSurfaceStandardStrong};
      transition: background-color ${motion.fast} ease;
    }
  }

  &.secondary {
    color: ${style.theme.textDefault};
    outline: ${borderWidth.s} solid ${style.theme.textDefault};
    outline-offset: -2px;
    background: ${style.theme.backgroundSurfacePrimary};

    &:disabled, &:disabled:hover {
      color: ${style.theme.textDisabled};
      outline-color: ${style.theme.backgroundSurfaceTertiaryStrong};
      background: ${style.theme.backgroundSurfaceTertiary};
    }

    &:hover {
      background: ${style.theme.backgroundSurfaceStandardSubtle};
    }
  }

  &.plain {
    color: ${style.theme.textDefault};
    background: transparent;
    outline: 1px solid transparent;
    outline-offset: -1px;
    border: none;

    &:hover {
      background: ${style.theme.backgroundSurfaceStandardSubtle};
    }

    &:disabled, &:disabled:hover {
      color: ${style.theme.textDisabled};
    }
  }

  &.plain-text {
    color: ${style.theme.textDefault};
    background: transparent;
    padding: 0;

    &:hover {
      color: ${style.theme.textAccent};
    }

    &:disabled, &:disabled:hover {
      color: ${style.theme.textDisabled};
      border: none;
    }
  }
  
  &.plain-accent {
    color: ${style.theme.textAccent};
    background: transparent;

    &:hover {
      color: ${style.theme.textDefault};
    }
    
    &:disabled, &:disabled:hover {
      color: ${style.theme.textDisabled};
    }
  }

  &.tab {
    padding: 8px 0;
    height: 100%;
    display: flex;
    align-items: center;
    color: ${style.theme.textDefault};
    border: none;
    background: transparent;
    border-bottom: 2px solid ${style.theme.borderSurface};
    transition: border-color ${motion.fast} ease;
  
    ${fragments.textStyle.body.s.condensedBold};
    overflow: hidden;
    
    &.size-m {
      padding: ${spacing.sp8} 0;
    }
  
    &.active {
      color: ${style.theme.textAccent};
      border-bottom: 2px solid ${style.theme.borderDanger};
    } 
  }
  
  &.flex {
    display: flex;
    align-items: center;
    gap: ${spacing.sp8};
  }
`

export type ButtonProps = {
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  kind?: 'accent' | 'secondary' | 'plain' | 'plain-text' | 'tab' | 'primary' | 'plain-accent' | 'custom'
  className?: string
  title?: ReactNode
  children?: ReactNode
  forceTooltipOpen?: boolean
  allowTooltipWhenDisabled?: boolean
} & Omit<ComponentProps<'button'>, 'title'>

export const Button: FC<ButtonProps> = ({ ref, size, className, kind, children, forceTooltipOpen, allowTooltipWhenDisabled, title, ...props }) => {
  size ??= 'm'
  kind ??= 'accent'

  if (!title) {
    return (
      <button className={cx(ButtonClass, `size-${size}`, kind, className)} type="button" {...props} ref={ref}>
        {children}
      </button>
    )
  }

  return (
    <HoverTooltip.button
      className={cx(ButtonClass, `size-${size}`, kind, className)}
      type="button"
      title={title}
      forceOpen={forceTooltipOpen}
      allowTooltipWhenDisabled={allowTooltipWhenDisabled}
      ref={ref}
      {...props}
    >
      {children}
    </HoverTooltip.button>
  )
}
